import React, { useEffect, useState } from "react";
import { PlayCircle } from "lucide-react";
import api from "@/lib/api";

/**
 * Reusable thumbnail renderer.
 * Fetches /videos/:id/thumbnail as a blob (auth via api interceptor) and shows it.
 * Falls back to a gradient tile with a play icon when no thumbnail exists.
 */
export default function VideoThumbnail({ videoId, youtubeId, hasThumbnail, className = "" }) {
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (youtubeId) {
      setUrl(`https://img.youtube.com/vi/${encodeURIComponent(youtubeId)}/hqdefault.jpg`);
      return;
    }
    if (hasThumbnail === false) return;
    let objectUrl = null;
    let cancelled = false;
    api.get(`/videos/${videoId}/thumbnail`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [videoId, youtubeId, hasThumbnail]);

  if (url && !failed) {
    return (
      <div className={`relative ${className}`} data-testid={`video-thumb-${videoId}`}>
        <img
          src={url}
          alt=""
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
          <PlayCircle className="w-16 h-16 text-white/95 drop-shadow-lg" />
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`video-thumb-fallback-${videoId}`}
      className={`bg-gradient-to-br from-sky to-grape flex items-center justify-center ${className}`}
    >
      <PlayCircle className="w-16 h-16 text-white/90" />
    </div>
  );
}
